<template>
	<div
		class="fixed top-[calc(var(--tg-safe-area-inset-top,0px)+var(--tg-content-safe-area-inset-top,0px))] right-0 bottom-0 left-0"
	>
		<StickerPreloaderHeader />
		<LogoContainer />
		<Transition
			enterActiveClass="transition-all duration-500"
			leaveActiveClass="transition-all duration-500"
			enterFromClass="opacity-0 -mt-14"
			enterToClass="opacity-100 mt-0"
			leaveToClass="opacity-0 -mt-14"
			leaveFromClass="opacity-100 mt-0"
		>
			<RainToast v-if="rainVisible" />
		</Transition>
		<div
			class="absolute right-0 bottom-0 left-0 flex flex-col transition-all duration-500"
			:class="{
				'top-0': !rainVisible,
				'top-5.25': rainVisible && !rain.show,
				'top-17.5': rainVisible && rain.show
			}"
			v-if="!web"
		>
			<Transition
				enterActiveClass="transition-all duration-500"
				leaveActiveClass="transition-all duration-500"
				enterFromClass="opacity-0 -mt-14"
				enterToClass="opacity-100 mt-0"
				leaveToClass="opacity-0 -mt-14"
				leaveFromClass="opacity-100 mt-0"
			>
				<TheTopNavbar
					v-if="layout.topNavbar"
					@touchstart.passive="onNavbarTouchStart"
					@touchend.passive="onNavbarTouchEnd"
				/>
			</Transition>
			<div
				class="flex-1 overflow-x-hidden overflow-y-scroll bg-[#000] transition-all"
				style="padding-bottom: calc(var(--padding-bottom) + 2rem)"
				:class="{ 'pt-20': layout.topNavbar, 'pt-10': !layout.topNavbar }"
				id="view"
			>
				<RouterView />
			</div>
			<TheBottomNavbar v-if="layout.bottomNavbar" />
			<DepositModal v-model="layout.depositModal" />
			<ActivateModal v-model="layout.activateModal" />
			<FairInfoModal v-model="layout.fairInfoModal" />
			<TheToastArea />
		</div>
		<WebAppOnly v-if="web" />
	</div>
</template>
<script setup lang="ts">
import { watch, nextTick, ref, computed } from 'vue'
import TheBottomNavbar from './components/TheBottomNavbar.vue'
import TheTopNavbar from './components/TheTopNavbar.vue'
import { useLayout } from './stores/layout'
import { useUser } from './stores/user'
import { WebApp } from '@grammyjs/web-app'
import { useRoute, useRouter } from 'vue-router'
import DepositModal from './components/modals/DepositModal.vue'
import { useBattles } from './stores/battles'
import StickerPreloaderHeader from './components/StickerPreloaderHeader.vue'
import TheToastArea from './components/TheToastArea.vue'
import { useI18n } from 'vue-i18n'
import ActivateModal from './components/modals/ActivateModal.vue'
import FairInfoModal from './components/modals/FairInfoModal.vue'
import WebAppOnly from './components/WebAppOnly.vue'
import RainToast from './components/RainToast.vue'
import LogoContainer from './components/LogoContainer.vue'
import { useRain } from './stores/rain'

useBattles()

const layout = useLayout()
const user = useUser()
const router = useRouter()

const { locale, t } = useI18n({
	useScope: 'global',
	messages: {
		en: {
			writeAccessRequired:
				'The app requires permission to send messages to you to function properly.'
		},
		ru: {
			writeAccessRequired:
				'Для правильной работы приложения требуется разрешение на отправку сообщений вам.'
		}
	}
})

const web = ref(false)

const waitForWriteAccess = async () => {
	if (WebApp.initDataUnsafe.user?.allows_write_to_pm) {
		return true
	}

	return new Promise<boolean>((res) => {
		WebApp.requestWriteAccess((firstTry) => {
			if (firstTry) {
				return res(true)
			}

			WebApp.showConfirm(t('writeAccessRequired'), (ok) => {
				if (!ok) {
					return res(false)
				}

				WebApp.requestWriteAccess((secondTry) => {
					res(secondTry)
				})
			})
		})
	})
}

const initTMA = async () => {
	if (
		WebApp.platform === 'unknown' &&
		!import.meta.env.DEV &&
		!localStorage.getItem('force-webapp')
	) {
		web.value = true
		return
	}

	WebApp.disableVerticalSwipes()
	WebApp.setBackgroundColor('#000')
	WebApp.setHeaderColor('#000')
	WebApp.setBottomBarColor('#141414')
	WebApp.BackButton.onClick(() => router.back())
	WebApp.enableClosingConfirmation()

	const language = WebApp?.initDataUnsafe?.user?.language_code

	locale.value = 'en'

	if (language && ['en', 'ru'].includes(language)) {
		locale.value = language as 'en' | 'ru'
	}

	initFullscreen()

	if (
		WebApp.platform === 'unknown' &&
		!import.meta.env.DEV &&
		!localStorage.getItem('force-webapp')
	) {
		const result = await waitForWriteAccess()

		if (!result) {
			user.disableStars()
		}
	}

	const signedUp = await user.auth()

	await user.getMe()

	if (signedUp) {
		router.replace('/onboarding')
	} else if (WebApp.initDataUnsafe?.start_param) {
		if (WebApp.initDataUnsafe.start_param.includes('battle-')) {
			router.replace('/battles')
			router.push(
				`/battle/${WebApp.initDataUnsafe.start_param.replace('battle-', '')}`
			)
		}

		if (WebApp.initDataUnsafe.start_param.includes('case-')) {
			router.replace('/cases')
			router.push(
				`/case/${WebApp.initDataUnsafe.start_param.replace('case-', '')}`
			)
		}
	}

	WebApp.ready()
}

const initFullscreen = () => {
	if (['ios', 'android'].includes(WebApp.platform)) {
		if (WebApp.isFullscreen) {
			return
		}

		try {
			WebApp.requestFullscreen()
		} catch {}
		return
	}

	if (!WebApp.isFullscreen) {
		return
	}

	try {
		WebApp.exitFullscreen()
	} catch {}
}

initTMA()

const route = useRoute()

watch(
	route,
	() => {
		// Try to detect if there is a history entry to go back to
		const canGoBack =
			router.options.history?.state?.back !== null &&
			router.options.history?.state?.back !== undefined
		const isSecondLevelRoute =
			route.path.split('/').filter(Boolean).length > 1 ||
			['/cases', '/battles'].includes(route.path)

		if (canGoBack && isSecondLevelRoute) {
			if (WebApp.BackButton.isVisible) {
				return
			}

			WebApp.BackButton.show()

			return
		}

		if (!WebApp.BackButton.isVisible) {
			return
		}

		WebApp.BackButton.hide()
	},
	{ immediate: true }
)

const rain = useRain()

let navbarTouchStartY = 0

const onNavbarTouchStart = (e: TouchEvent) => {
	navbarTouchStartY = e.touches[0].clientY
}

const onNavbarTouchEnd = (e: TouchEvent) => {
	const deltaY = e.changedTouches[0].clientY - navbarTouchStartY
	if (deltaY > 30) {
		rain.open()
	} else if (deltaY < -30) {
		rain.close()
	}
}

const rainVisible = computed(() => layout.topNavbar && rain.available)
</script>
